/**
 * LiteModeContext - Performance/Accessibility animation mode
 * 
 * Auto-detects need for reduced animations based on:
 * - OS prefers-reduced-motion setting
 * - WebGL software renderer detection (no GPU acceleration)
 * 
 * Allows manual override via toggle. Persists to localStorage.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'retrostore_lite_mode';

const LiteModeContext = createContext(null);

/**
 * Hook to access lite mode state
 * @returns {{ isLiteMode: boolean, setLiteMode: (value: boolean) => void, isAutoDetected: boolean, userHasChosen: boolean }}
 */
export const useLiteMode = () => {
  const context = useContext(LiteModeContext);
  if (!context) {
    throw new Error('useLiteMode must be used within a LiteModeProvider');
  }
  return context;
};

/**
 * Detect if WebGL is using a software renderer (no GPU acceleration)
 * This is the most reliable way to detect if hardware acceleration is unavailable
 * @returns {boolean} true if software rendering detected (should enable lite mode)
 */
const detectSoftwareRenderer = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) {
      // WebGL not available at all - definitely software rendering
      console.log('[LiteMode] WebGL not available - enabling lite mode');
      return true;
    }

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    
    if (debugInfo) {
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      
      console.log('[LiteMode] WebGL Renderer:', renderer);
      console.log('[LiteMode] WebGL Vendor:', vendor);
      
      // Check for known software renderers
      const rendererLower = renderer.toLowerCase();
      const vendorLower = vendor.toLowerCase();
      
      const softwareIndicators = [
        'swiftshader',      // Chrome's software renderer
        'llvmpipe',         // Mesa's software renderer (Linux)
        'softpipe',         // Mesa's software renderer (Linux)
        'software',         // Generic software indicator
        'microsoft basic',  // Windows Basic Render Driver
        'mesa',             // Often indicates software on some systems
        'vmware',           // Virtual machine (usually no GPU)
        'virtualbox',       // Virtual machine
      ];
      
      for (const indicator of softwareIndicators) {
        if (rendererLower.includes(indicator) || vendorLower.includes(indicator)) {
          console.log(`[LiteMode] Software renderer detected: ${indicator}`);
          return true;
        }
      }
      
      // Also check if it's Google's SwiftShader (common fallback)
      if (vendorLower.includes('google') && rendererLower.includes('angle')) {
        // ANGLE with Google could be SwiftShader
        if (rendererLower.includes('swiftshader') || rendererLower.includes('direct3d')) {
          // Direct3D through ANGLE is usually hardware accelerated, so this is OK
          // But SwiftShader through ANGLE is software
          if (rendererLower.includes('swiftshader')) {
            console.log('[LiteMode] SwiftShader via ANGLE detected');
            return true;
          }
        }
      }
    }
    
    // Clean up
    const loseContext = gl.getExtension('WEBGL_lose_context');
    if (loseContext) {
      loseContext.loseContext();
    }
    
    return false;
  } catch (e) {
    console.warn('[LiteMode] WebGL detection failed:', e);
    // If detection fails, don't assume software rendering
    return false;
  }
};

/**
 * Detect if user prefers reduced motion (OS setting)
 * @returns {boolean}
 */
const detectPrefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const LiteModeProvider = ({ children }) => {
  const [isLiteMode, setIsLiteModeState] = useState(false);
  const [isAutoDetected, setIsAutoDetected] = useState(false);
  const [userHasChosen, setUserHasChosen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize on mount - check localStorage or auto-detect
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    
    if (stored !== null) {
      // User has previously made a choice
      setIsLiteModeState(stored === 'true');
      setUserHasChosen(true);
      setIsInitialized(true);
      return;
    }

    // No stored preference - run auto-detection
    const prefersReducedMotion = detectPrefersReducedMotion();
    const isSoftwareRenderer = detectSoftwareRenderer();
    
    const shouldEnableLiteMode = prefersReducedMotion || isSoftwareRenderer;

    if (shouldEnableLiteMode) {
      setIsLiteModeState(true);
      setIsAutoDetected(true);
      console.log('[LiteMode] Auto-enabled:', { prefersReducedMotion, isSoftwareRenderer });
    } else {
      console.log('[LiteMode] Hardware acceleration detected - using modern mode');
    }
    
    setIsInitialized(true);
  }, []);

  // Apply .lite-mode class to <html> element
  useEffect(() => {
    if (!isInitialized) return;
    
    if (isLiteMode) {
      document.documentElement.classList.add('lite-mode');
    } else {
      document.documentElement.classList.remove('lite-mode');
    }
  }, [isLiteMode, isInitialized]);

  // Listen for OS preference changes (real-time updates)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleChange = (e) => {
      // Only auto-update if user hasn't manually chosen
      if (!userHasChosen) {
        setIsLiteModeState(e.matches);
        setIsAutoDetected(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [userHasChosen]);

  // Manual toggle function - stores to localStorage
  const setLiteMode = useCallback((value) => {
    setIsLiteModeState(value);
    setUserHasChosen(true);
    setIsAutoDetected(false);
    localStorage.setItem(STORAGE_KEY, String(value));
  }, []);

  const value = {
    isLiteMode,
    setLiteMode,
    isAutoDetected,
    userHasChosen
  };

  return (
    <LiteModeContext.Provider value={value}>
      {children}
    </LiteModeContext.Provider>
  );
};

export default LiteModeContext;
